import { State, Action, StateContext, NgxsOnInit, Selector, Store } from '@ngxs/store';
import { MemoryActions } from './memory.actions';
import {
  MorphStatsModel,
  MemoryStatsSummaryModel,
  GeoStatsModel,
} from '../models/memory-stats.model';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { MemoryGlobalStatsModel } from '../models/memory-global-stats.model';
import { MemoryService } from '../memory.service';
import { MemoryReport } from '../models/memory-report.model';
import { StoryState } from 'src/app/story/state/story.state';
import { get } from 'lodash';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { Injectable } from '@angular/core';
import { HumanInterface } from 'src/app/engine/models/human.interface';

declare const Human: HumanInterface;

export class MemoryStateModel {
  public compressionSupport: {
    geometry: boolean;
    morph: boolean;
    texture: boolean;
  };

  public morphStats: MemoryStatsSummaryModel<MorphStatsModel>;
  public geoStats: MemoryStatsSummaryModel<GeoStatsModel>;
  public globalStats: MemoryGlobalStatsModel;

  public geoStatsReport: MemoryReport;
  public morphStatsReport: MemoryReport;
  public textureStatsReport: MemoryReport;
  public downloadReport: MemoryReport;

  public firstChapterDownloadSize: number;
}

// export const initialViewState: ViewStateModel = {
export const initialMemoryState: MemoryStateModel = {
  compressionSupport: {
    geometry: false,
    morph: false,
    texture: false,
  },
  morphStats: null,
  geoStats: null,

  geoStatsReport: null,
  morphStatsReport: null,
  textureStatsReport: null,
  downloadReport: null,

  globalStats: {
    memory: 0.0,
    textureMemory: 0.0,
    geometryMemory: 0.0,
    morphMemory: 0.0,

    downloadSize: 0,
    uncompressedGeos: 0,
    uncompressedMorphs: 0,
    uncompressedTextures: 0,

    geometryDownloadTotal: 0,
    textureDownloadTotal: 0,
    jsonDownloadTotal: 0,
  },

  firstChapterDownloadSize: 0,
};

@State<MemoryStateModel>({
  name: 'memory',
  defaults: initialMemoryState,
})
@Injectable()
export class MemoryState implements NgxsOnInit {
  constructor(
    private engineService: EngineService,
    private memoryService: MemoryService,
    private store: Store,
  ) {}

  @Selector()
  static getGlobalStats(state: MemoryStateModel) {
    return state.globalStats;
  }

  @Selector()
  static getFirstChapterDownloadSize(state: MemoryStateModel) {
    return state.firstChapterDownloadSize;
  }

  @Action(MemoryActions.Reset)
  reset(ctx: StateContext<MemoryStateModel>, action: MemoryActions.Reset) {
    ctx.setState({ ...initialMemoryState });
  }

  @Action(MemoryActions.Update)
  update(ctx: StateContext<MemoryStateModel>, action: MemoryActions.Update) {
    const geoStats = this.memoryService.updateGeoStats();
    const morphStats = this.memoryService.updateMorphStats();
    const globalStats = this.memoryService.updateGlobalStats(geoStats, morphStats);

    if (action.isFirstChapter) {
      ctx.patchState({
        firstChapterDownloadSize: this.memoryService.getFirstChapterDownloadSize(globalStats),
      });
    }

    const geoStatsReport = this.memoryService.buildSceneGeometryReport(geoStats);
    const morphStatsReport = this.memoryService.buildSceneMorphReport(morphStats);
    const textureStatsReport = this.memoryService.buildSceneTextureReport();
    const downloadReport = this.memoryService.buildSceneDownloadReport(globalStats);

    // 1048576 bytes in a Megabyte (1024x1024)

    ctx.setState({
      ...ctx.getState(),
      geoStats: geoStats,
      morphStats: morphStats,
      globalStats: globalStats,

      geoStatsReport: geoStatsReport,
      morphStatsReport: morphStatsReport,
      textureStatsReport: textureStatsReport,
      downloadReport: downloadReport,
    });

    if (action.logToConsole) {
      this.logMemoryState(ctx.getState());
    }
  }

  ngxsOnInit(context: StateContext<MemoryStateModel>) {
    // Always track download size
    Human.stats.trackingDownloadSize = true;

    this.engineService.getEvent(EventsEnum.GraphAssetAttachFinish).subscribe(() => {
      const chapterId = get(this.store.selectSnapshot(StoryState), 'loadingChunk.chapterId', '');
      const firstChapterId = get(this.store.selectSnapshot(StoryChaptersState), 'chapters[0].id');
      const isFirstChapter = chapterId === firstChapterId;

      context.dispatch(new MemoryActions.Update(isFirstChapter));
    });
  }

  private makeReportTableFriendly(report: MemoryReport) {
    return report.reports.reduce((result, _report) => {
      result = {
        ...result,
        [_report.name]: _report.value,
      };

      return result;
    }, {});
  }

  private logMemoryState(state: MemoryStateModel) {
    console.groupCollapsed('Memory Usage');

    console.group('Totals');
    console.table({
      'Total Memory': state.globalStats.memory,
      'Total Geo Memory': state.globalStats.geometryMemory,
      'Total Morph Memory': state.globalStats.morphMemory,
      'Total Texture Memory': state.globalStats.textureMemory,
    });

    console.groupEnd();

    console.group('Uncompressed Assets');
    console.table({
      'Uncompressed Geos': state.globalStats.uncompressedGeos,
      'Uncompressed Morphs': state.globalStats.uncompressedMorphs,
      'Uncompressed Textures': state.globalStats.uncompressedTextures,
    });
    console.groupEnd();

    // morphStatsReport.reports[0].name
    console.group('Geometry Stats');
    console.table(this.makeReportTableFriendly(state.geoStatsReport));
    console.groupEnd();

    console.group('Morph Stats');
    console.table(this.makeReportTableFriendly(state.morphStatsReport));
    console.groupEnd();

    console.group('Texture Stats');
    console.table(this.makeReportTableFriendly(state.textureStatsReport));
    state.textureStatsReport.reports.forEach((report) => {
      if (report.reports.length) {
        console.group(report.name);
        console.table(this.makeReportTableFriendly(report));
        console.groupEnd();
      }
    });
    console.groupEnd();

    console.group('Download Size');
    console.table(this.makeReportTableFriendly(state.downloadReport));
    console.groupEnd();

    console.groupCollapsed();
  }
}
